import { useState } from 'react'
import { Star, Loader2, Send } from 'lucide-react'
import { reviewApi, Review } from '../../services/api'
import { showNotification } from '../../utils/telegram'
import { t } from '../../i18n/translations'

interface ReviewFormProps {
  revieweeId: string
  reviewType: 'seller' | 'buyer'
  orderId?: string
  onSuccess?: (review: Review) => void
  onCancel?: () => void
}

export function ReviewForm({ revieweeId, reviewType, orderId, onSuccess, onCancel }: ReviewFormProps) {
  const [rating, setRating] = useState(0)
  const [hoverRating, setHoverRating] = useState(0)
  const [comment, setComment] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async () => {
    if (rating === 0) return
    setSubmitting(true)
    setError(null)
    try {
      const review = await reviewApi.createReview({
        reviewee_id: revieweeId,
        review_type: reviewType,
        rating,
        comment: comment.trim() || undefined,
        order_id: orderId,
      })
      showNotification(t('reviews.submitted'), 'success')
      onSuccess?.(review)
    } catch (err: any) {
      // The API layer throws an Error carrying the backend message. A duplicate
      // review returns 409 with an "already reviewed" style message — surface a
      // friendly, translated string for that case instead of the raw error.
      const raw = String(err?.message || '')
      const isDuplicate = err?.status === 409 || /already/i.test(raw)
      const msg = isDuplicate
        ? t('reviews.alreadyReviewed')
        : (raw || 'Failed to submit review')
      setError(msg)
      showNotification(msg, 'error')
    } finally {
      setSubmitting(false)
    }
  }

  const label = reviewType === 'seller' ? t('reviews.sellerRating') : t('reviews.buyerRating')

  return (
    <div className="bg-white rounded-2xl p-5 shadow-sm">
      <h3 className="text-sm font-semibold text-gray-800 mb-3">{label}</h3>

      {/* Stars */}
      <div className="flex items-center gap-1 mb-3">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => setRating(star)}
            onMouseEnter={() => setHoverRating(star)}
            onMouseLeave={() => setHoverRating(0)}
            className="p-0.5 transition-all active:scale-90"
          >
            <Star
              className={`w-7 h-7 transition-colors ${
                (hoverRating || rating) >= star
                  ? 'text-yellow-400 fill-yellow-400'
                  : 'text-gray-300'
              }`}
            />
          </button>
        ))}
        {rating > 0 && (
          <span className="ml-2 text-sm font-semibold text-gray-600">
            {rating}/5
          </span>
        )}
      </div>

      {/* Comment */}
      <textarea
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        placeholder={t('reviews.commentPlaceholder')}
        rows={3}
        maxLength={1000}
        className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 bg-gray-50
          text-sm text-gray-800 placeholder-gray-400 resize-none
          focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent
          transition-all"
      />

      {/* Error */}
      {error && (
        <p className="text-sm text-red-500 mt-2">{error}</p>
      )}

      {/* Actions */}
      <div className="flex gap-2 mt-3">
        {onCancel && (
          <button
            onClick={onCancel}
            disabled={submitting}
            className="flex-1 py-2.5 rounded-xl border-2 border-gray-200 text-gray-600
              font-semibold text-sm hover:bg-gray-50 active:scale-[0.98] transition-all"
          >
            {t('reviews.cancel')}
          </button>
        )}
        <button
          onClick={handleSubmit}
          disabled={submitting || rating === 0}
          className="flex-1 py-2.5 rounded-xl bg-blue-500 text-white font-semibold text-sm
            hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed
            active:scale-[0.98] transition-all shadow-lg shadow-blue-500/20
            flex items-center justify-center gap-2"
        >
          {submitting ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Send className="w-4 h-4" />
          )}
          {t('reviews.submit')}
        </button>
      </div>
    </div>
  )
}

// Star display (read-only) for showing average ratings
export function StarRating({ rating, count, size = 'sm' }: { rating: number; count?: number; size?: 'sm' | 'md' }) {
  const starSize = size === 'md' ? 'w-5 h-5' : 'w-4 h-4'
  return (
    <span className="inline-flex items-center gap-1">
      <Star className={`${starSize} text-yellow-400 fill-yellow-400`} />
      <span className="font-semibold text-gray-800">{rating > 0 ? rating.toFixed(1) : '—'}</span>
      {count != null && (
        <span className="text-gray-400">({count})</span>
      )}
    </span>
  )
}

// Review list item
export function ReviewItem({ review }: { review: Review }) {
  const reviewerName = review.reviewer?.first_name
    || review.reviewer?.username
    || `User ${review.reviewer?.telegram_id?.toString().slice(-4)}`

  return (
    <div className="py-3 border-b border-gray-100 last:border-0">
      <div className="flex items-center justify-between mb-1">
        <span className="text-sm font-semibold text-gray-800">{reviewerName}</span>
        <span className="text-xs text-gray-400">
          {new Date(review.created_at).toLocaleDateString()}
        </span>
      </div>
      <div className="flex items-center gap-0.5 mb-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`w-3.5 h-3.5 ${
              review.rating >= star ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'
            }`}
          />
        ))}
      </div>
      {review.comment && (
        <p className="text-sm text-gray-600 leading-relaxed">{review.comment}</p>
      )}
    </div>
  )
}
